/**
 * Floor slabs and the 16-case NESW wall kit (lifted from the dungeon-floors
 * prototype's `dungeon-world.js`). The slabs draw at both tiers; the wall
 * boxes and trim at Off only (Medium draws the merged rock mass,
 * `rock-mass.ts`), and the border rock at Medium only. Every wall is a closed box; a
 * wall with open ground within two tiles north is cut to a low stub so the
 * south-facing camera sees into rooms. Exposed edges (the mask's open sides)
 * get a trim lip and concave corners a cap, so a floor reads in flat colour.
 */
import type { Floor } from '@ab/game-core';
import type { Theme } from '@ab/content-schema';
import type * as THREE from 'three';
import { PartBatch, part } from './batch.ts';
import { presentation } from './constants.ts';
import { innerCornerMask, isOpen, tileIndex, tileRandom, wallMask } from './grid.ts';
import { type Palette, shaded } from './palette.ts';

export type WallRecord = {
  readonly x: number;
  readonly y: number;
  readonly index: number;
  /** Wall neighbours, N=1 E=2 S=4 W=8. */
  readonly mask: number;
  /** Concave notches, NE=1 SE=2 SW=4 NW=8. */
  readonly innerCorners: number;
  readonly height: number;
  readonly cutaway: boolean;
};

export type FloorParts = { readonly floor: PartBatch; readonly wall: PartBatch; readonly trim: PartBatch; readonly walls: readonly WallRecord[] };

/** Per-tile colour variation from the theme's grain strength, seeded by the floor. */
const grained = (base: THREE.Color, theme: Theme, seed: number, x: number, y: number, salt: number): THREE.Color => {
  const grain = theme.tileMaterials.grainStrengthPercent / 100;
  return shaded(base, 1 + (tileRandom(seed, x, y, salt) - 0.5) * presentation.floor.grainSpread * grain);
};

export const buildFloorParts = (floor: Floor, seed: number, theme: Theme, palette: Palette): FloorParts => {
  const floorBatch = new PartBatch('floor');
  const wallBatch = new PartBatch('walls');
  const trimBatch = new PartBatch('trim');
  const walls: WallRecord[] = [];
  const { slabY, slabHeight } = presentation.floor;
  const { height: fullHeight, cutawayHeight, trimHeight: th, trimWidth: tw, cornerSize: cs } = presentation.wall;
  for (let y = 0; y < floor.height; y += 1) {
    for (let x = 0; x < floor.width; x += 1) {
      const index = tileIndex(floor, x, y);
      const tile = floor.tiles[index];
      if (tile === 'floor') floorBatch.add(part(index, x, slabY, -y, [1, slabHeight, 1], grained(palette.floor, theme, seed, x, y, 2)));
      if (tile !== 'wall') continue;
      const mask = wallMask(floor, x, y);
      const innerCorners = innerCornerMask(floor, x, y);
      const cutaway = isOpen(floor, x, y + 1) || isOpen(floor, x, y + 2);
      const h = cutaway ? cutawayHeight : fullHeight;
      walls.push({ x, y, index, mask, innerCorners, height: h, cutaway });
      wallBatch.add(part(index, x, h / 2, -y, [1, h, 1], grained(palette.wall, theme, seed, x, y, 3)));
      const lipY = h + th / 2;
      // Open sides of the NESW mask: a lip along that top edge.
      if (!(mask & 1)) trimBatch.add(part(index, x, lipY, -y - 0.5 + tw / 2, [1, th, tw], palette.trim));
      if (!(mask & 2)) trimBatch.add(part(index, x + 0.5 - tw / 2, lipY, -y, [tw, th, 1], palette.trim));
      if (!(mask & 4)) trimBatch.add(part(index, x, lipY, -y + 0.5 - tw / 2, [1, th, tw], palette.trim));
      if (!(mask & 8)) trimBatch.add(part(index, x - 0.5 + tw / 2, lipY, -y, [tw, th, 1], palette.trim));
      // Concave notches: a cap on the corner that meets the open diagonal.
      const corners: readonly (readonly [number, number, number])[] = [
        [1, 1, -1],
        [2, 1, 1],
        [4, -1, 1],
        [8, -1, -1],
      ];
      for (const [bit, dx, dz] of corners) {
        if (innerCorners & bit) trimBatch.add(part(index, x + dx * (0.5 - cs / 2), lipY, -y + dz * (0.5 - cs / 2), [cs, th, cs], palette.trim));
      }
    }
  }
  // Border rock beyond the floor edge (Medium only; always in fog): tall rear and side masses and a low
  // camera-side apron, so portrait frames never show the background void (`dungeon-world.js`).
  const b = presentation.medium.border;
  const border = shaded(palette.wall, (1 + theme.tileMaterials.capFadeFloorPercent / 100) / 2);
  const w = floor.width;
  const h = floor.height;
  const cx = (w - 1) / 2;
  const mediumOnly = { mediumOnly: true };
  floorBatch.add(part(-1, cx, b.rear[0] / 2, -(h - 0.5) - b.rear[2] / 2, [w + b.margin, b.rear[0], b.rear[2]], border, mediumOnly));
  floorBatch.add(part(-1, cx, b.apron[0] / 2, b.apron[2] / 2 + 0.5, [w + b.margin, b.apron[0], b.apron[2]], border, mediumOnly));
  for (const x of [-0.5 - b.side[1] / 2, w - 0.5 + b.side[1] / 2]) floorBatch.add(part(-1, x, b.side[0] / 2, -(h - 1) / 2, [b.side[1], b.side[0], h], border, mediumOnly));
  return { floor: floorBatch, wall: wallBatch, trim: trimBatch, walls };
};
